import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { useApi } from 'service/useApi'
import { logError } from 'utils/logger'

type ShipUserPreferenceModel = {
  id: string
  value: {
    useLegacyUI: boolean
  }
}

type ShipUserPreferenceState = {
  isShipUserPreferenceLoading: boolean
  shipUserPreference?: ShipUserPreferenceModel
  removeShipUserPreference: (shipUserPreferenceId: string) => Promise<void>
  getShipUserPreference: () => Promise<void>
}

const ShipUserPreferenceContext = createContext<ShipUserPreferenceState | null>(null)

export const ShipUserPreferenceContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [shipUserPreference, setShipUserPreference] = useState<ShipUserPreferenceModel>()
  const [isShipUserPreferenceLoading, setIsLoading] = useState(true)
  const API = useApi()

  const getShipUserPreference = useCallback(async () => {
    setIsLoading(true)
    try {
      const shipUserPreferenceFromService = await API.userProfileCore.shipUserPreference.getShipUserPreference()
      setShipUserPreference(shipUserPreferenceFromService?.owner?.[0])
    } catch (ignoreError) {
      logError(`Failed to load Ship User Preference`)
    } finally {
      setIsLoading(false)
    }
  }, [API])

  const removeShipUserPreference = useCallback(
    async (shipUserPreferenceId: string): Promise<void> => {
      await API.userProfileCore.shipUserPreference.removeShipUserPreference(shipUserPreferenceId).then(() => {
        setShipUserPreference({
          id: '',
          value: {
            useLegacyUI: false,
          },
        })
      })
    },
    [API]
  )

  const providerValue = useMemo(
    () => ({
      isShipUserPreferenceLoading,
      shipUserPreference,
      removeShipUserPreference,
      getShipUserPreference,
    }),
    [isShipUserPreferenceLoading, shipUserPreference, removeShipUserPreference, getShipUserPreference]
  )

  return <ShipUserPreferenceContext.Provider value={providerValue}>{children}</ShipUserPreferenceContext.Provider>
}

export const useShipUserPreference = (): ShipUserPreferenceState => {
  const context = useContext(ShipUserPreferenceContext)

  if (!context) {
    throw new Error('Must use useShipUserPreference within a ShipUserPreferenceContext')
  }

  return context
}
